// =============================================================================
// Shared Types for Test Cases
// =============================================================================
//
// This file consolidates types related to test cases that are used across
// multiple parts of the codebase (editor, test harness, API handlers, etc.)
// =============================================================================

// =============================================================================
// Problem Test Case Types (frontend display)
// =============================================================================

export interface ProblemTestCaseInput {
  name: string
  example: unknown
}

export interface ProblemTestCase {
  _id: string
  id?: string
  args: unknown[]
  inputs: ProblemTestCaseInput[]
  testCaseIntent: string
  expected: unknown
  expectedInputsAfter?: Record<string, unknown> | null
}

/**
 * Operation in a class-based test case.
 *
 * Used in:
 * - Test Cases pane for editing/displaying class operations
 * - MDX frontmatter for class-based examples
 * - Run mode execution for class-based problems
 */
export interface ClassOperationItem {
  type?: 'operation' // Optional for backward compatibility (missing = operation)
  method: string
  args: string // JSON array as string
  expected: string // JSON value as string
  expectsError?: boolean // If true, operation should raise an exception
}

/**
 * Comment in a class-based test case.
 * Comments annotate the operation sequence but have no effect on test execution.
 */
export interface ClassCommentItem {
  type: 'comment'
  text: string // Comment text (multi-line supported with \n separators)
}

/**
 * Union type for items in a class-based test case's operations array.
 */
export type ClassOperation = ClassOperationItem | ClassCommentItem

/**
 * Type guard to check if a ClassOperation is a comment
 */
export function isClassComment(item: ClassOperation): item is ClassCommentItem {
  return item.type === 'comment'
}

/**
 * Type guard to check if a ClassOperation is an operation
 */
export function isClassOperation(
  item: ClassOperation,
): item is ClassOperationItem {
  return item.type !== 'comment'
}

// =============================================================================
// Structured Failure Payload (LG-165)
// =============================================================================

/**
 * A single stack frame from the user's own code.
 *
 * Lines are 1-based and editor-relative: the test harness has already
 * subtracted any generated preamble, so `line` maps directly onto the
 * user's editor buffer.
 */
export interface UserFrame {
  /** 1-based line in the user's editor */
  line: number
  /** e.g. 'two_sum', or '<module>' for module-level code */
  functionName: string
}

/**
 * Structured description of why a test case (or the whole program) failed.
 *
 * Produced by the Python runner. Every future language adapter must emit
 * this same shape — it seeds the LG-128 shared runner interface.
 */
export interface TestFailure {
  kind: 'wrong_answer' | 'runtime_error'
  /** Exception class name, e.g. 'NameError' (runtime_error only) */
  errorType?: string
  /** e.g. "name 'num' is not defined. Did you mean: 'nums'?" */
  message?: string
  /**
   * Frames from the user's code only, editor-relative, innermost last.
   * Empty array = frameless error (e.g. a bad return value exploding
   * inside a comparator) — the UI shows the message with no line claim.
   */
  userFrames?: UserFrame[]
}

/**
 * Test case format for function-based problems (for Run mode).
 *
 * Used by the Test Cases pane to pass test cases to the execution API.
 */
export interface RunTestCase {
  inputs: Record<string, string>
  expectedOutput: string
  expectedInputsAfter?: Record<string, string>
}

/**
 * Test case format for class-based problems (for Run mode).
 *
 * Used by the Test Cases pane to pass test cases to the execution API.
 */
export interface RunClassTestCase {
  constructorArgs: string // JSON array as string
  operations: ClassOperation[]
}

/**
 * Result of a single operation in a class-based test.
 *
 * Returned by the test harness after executing class-based tests.
 * Used for displaying operation-by-operation results in the UI.
 *
 * Note: `method` and `args` may be undefined in legacy results
 * (before we added operation-level tracking).
 */
export interface OperationTestResult {
  passed: boolean
  method?: string
  args?: unknown[]
  expected?: unknown
  actual?: unknown
  /** Structured failure details for a failed operation. */
  failure?: TestFailure
}

// =============================================================================
// Discriminated Union for Test Cases State
// =============================================================================

/**
 * Function-based test cases state.
 * Used when the problem requires a function implementation.
 */
export interface FunctionTestCasesState {
  type: 'function'
  cases: RunTestCase[]
}

/**
 * Class-based test cases state.
 * Used when the problem requires a class implementation.
 */
export interface ClassTestCasesState {
  type: 'class'
  cases: RunClassTestCase[]
}

/**
 * Raw test cases state.
 * Used when the problem uses admin-authored test scripts.
 */
export interface RawTestCasesState {
  type: 'raw'
  scripts: string[]
}

/**
 * Discriminated union for test cases state.
 *
 * This type ensures that test cases are always associated with their type,
 * making it impossible to accidentally read the wrong test case format.
 *
 * Usage:
 * ```typescript
 * if (state.type === 'function') {
 *   // TypeScript knows state.cases is RunTestCase[]
 *   handleRun({ testCases: state.cases })
 * } else if (state.type === 'class') {
 *   // TypeScript knows state.cases is RunClassTestCase[]
 *   handleRun({ classTestCases: state.cases })
 * } else {
 *   // TypeScript knows state.scripts is string[]
 *   handleRun({ rawTestScripts: state.scripts })
 * }
 * ```
 */
export type TestCasesState =
  | FunctionTestCasesState
  | ClassTestCasesState
  | RawTestCasesState
